import { riskCfg, healthColor, trendIcon, SIGNAL } from "@/lib/format";

export function RiskBadge({ risk, testid }) {
  const c = riskCfg(risk);
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c.dot }} />
      {c.label}
    </span>
  );
}

export function HealthBar({ score }) {
  const color = healthColor(score);
  return (
    <div className="flex items-center gap-2 tabular">
      <span className="text-sm font-semibold text-slate-900 w-7 text-right">{score}</span>
      <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export function TrendPill({ trend }) {
  const { Icon, cls } = trendIcon(trend);
  const label = { steigend: "steigend", sinkend: "sinkend", stabil: "stabil", inaktiv: "inaktiv" }[trend] || "—";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

export function SignalBadge({ signal }) {
  if (!signal || signal === "neutral") return null;
  const c = SIGNAL[signal];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
}

export function ConfidenceTag({ confidence }) {
  const cls = { Hoch: "text-emerald-700 bg-emerald-50 border-emerald-200",
                Mittel: "text-amber-700 bg-amber-50 border-amber-200",
                Niedrig: "text-slate-500 bg-slate-50 border-slate-200" }[confidence] || "text-slate-500 bg-slate-50 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      Confidence: {confidence}
    </span>
  );
}

/**
 * Reklamationen und Kulanzen nebeneinander – Beträge oben, der gemeinsame
 * Anteil am Umsatz darunter.
 *
 * Der Anteil ist der eigentliche Maßstab: 5.904 Euro Reklamation klingen viel,
 * sind bei einem Kunden mit 250.000 Euro Umsatz aber 2,4 Prozent. Bei einem
 * kleinen Labor wäre derselbe Betrag ein Alarmzeichen. Ohne die Prozentzahl
 * vergleicht man Beträge, die nicht vergleichbar sind.
 *
 * Warum beides zusammengerechnet: Getrennt sagen die Zahlen, woran es lag –
 * Reklamation heißt, etwas war fehlerhaft, Kulanz heißt, wir haben nachgegeben,
 * ohne dass es sein müsste. Für den Vergleich zweier Kunden zählt aber die
 * Summe, denn beides ist Umsatz, der nicht geblieben ist. Wer nur die 2,1 %
 * Reklamation sieht und die 1,8 % Kulanz daneben übersieht, hält einen
 * Vier-Prozent-Kunden für halb so teuer, wie er ist. Die Einzelanteile stehen
 * weiterhin in den Kurzhinweisen der jeweiligen Summe.
 *
 * Bezugsgröße ist der Gesamtumsatz des Kunden über alle erfassten Jahre, nicht
 * die letzten zwölf Monate – Reklamationen und der Umsatz, auf den sie sich
 * beziehen, fallen oft in verschiedene Zeiträume.
 */
export function prozent(wert) {
  return wert === null || wert === undefined
    ? null
    : `${wert.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;
}

/**
 * Beide Anteile zusammen.
 *
 * Bevorzugt den vom Server gerechneten Wert. Fehlt er – die Kennzahlen eines
 * Kunden können älter sein als diese Rechnung –, werden die Einzelanteile
 * addiert: Beide beziehen sich auf denselben Gesamtumsatz, die Summe ist
 * also zulässig und weicht höchstens um ein Zehntel Prozentpunkt ab.
 */
export function gesamtQuote(belastungQuote, reklamationQuote, kulanzQuote) {
  if (belastungQuote !== null && belastungQuote !== undefined) return belastungQuote;
  if (reklamationQuote == null && kulanzQuote == null) return null;
  return Math.round(((reklamationQuote || 0) + (kulanzQuote || 0)) * 10) / 10;
}

export function RekKulanz({ reklamation, kulanz, reklamationQuote, kulanzQuote,
                            belastungQuote, zeitraum = "letzten 12 Monaten",
                            sortiertNach = null, fmtEUR }) {
  const r = reklamation || 0;
  const k = kulanz || 0;
  const gesamt = (r > 0 || k > 0)
    ? gesamtQuote(belastungQuote, reklamationQuote, kulanzQuote) : null;

  // Was in den Kurzhinweisen steht. Ohne sie ist "4.443 € / 752 €" eine
  // Zahlenreihe, bei der man raten muss, welche Hälfte welche ist – und der
  // Zeitraum gehört dazu, sonst liest man Beträge aus zehn Jahren neben dem
  // Umsatz eines Jahres und hält das für vergleichbar.
  const hinweisR = r > 0
    ? `Reklamationssumme der ${zeitraum}: ${fmtEUR(r)}`
      + (reklamationQuote != null ? ` – ${prozent(reklamationQuote)} vom Umsatz desselben Zeitraums` : "")
    : `Keine Reklamationen in den ${zeitraum}`;
  const hinweisK = k > 0
    ? `Kulanzsumme der ${zeitraum}: ${fmtEUR(k)}`
      + (kulanzQuote != null ? ` – ${prozent(kulanzQuote)} vom Umsatz desselben Zeitraums` : "")
    : `Keine Kulanzen in den ${zeitraum}`;

  // Welche der drei Zahlen die Reihenfolge bestimmt.
  //
  // In der Zelle stehen Reklamationssumme, Kulanzsumme und der gemeinsame
  // Anteil. Sortiert ist immer nur nach einer davon - und wer die falsche
  // liest, haelt die Tabelle fuer kaputt: Ein Kunde mit 21,4 % steht dann
  // an zweiter Stelle, weil nach dem Betrag sortiert wurde, nicht nach dem
  // Anteil. Der Pfeil in der Kopfzeile sagt das zwar, aber beim Lesen der
  // Zahlen schaut niemand nach oben.
  const hervor = "rounded bg-slate-200/70 px-1 -mx-1";
  const markiert = (welche) => (sortiertNach === welche ? ` ${hervor}` : "");

  return (
    <span className="inline-block whitespace-nowrap text-right">
      <span className="block tabular">
        <span title={hinweisR + (sortiertNach === "reklamation"
                                 ? " · danach ist sortiert" : "")}
              className={(r > 0 ? "text-red-600 font-medium" : "text-slate-300")
                         + markiert("reklamation")}>
          {fmtEUR(r)}
        </span>
        <span className="text-slate-300"> / </span>
        <span title={hinweisK + (sortiertNach === "kulanz"
                                 ? " · danach ist sortiert" : "")}
              className={(k > 0 ? "text-amber-600 font-medium" : "text-slate-300")
                         + markiert("kulanz")}>
          {fmtEUR(k)}
        </span>
      </span>
      {gesamt != null && (
        <span className={`block text-[11px] tabular ${
                gesamt > 100 ? "text-red-600 font-medium" : "text-slate-500"}`
                + markiert("belastung")}
              data-testid="rekl-kulanz-gesamt"
              title={`Reklamationen und Kulanzen zusammen: ${prozent(gesamt)} `
                     + `vom Umsatz der ${zeitraum} – derselbe Zeitraum wie die `
                     + `Umsatzspalte daneben.`
                     + (sortiertNach === "belastung" ? " · danach ist sortiert" : "")
                     // Über 100 % sieht nach einem Rechenfehler aus und ist
                     // keiner: Eine Gutschrift kann sich auf Arbeit beziehen,
                     // die vor über einem Jahr abgerechnet wurde. Wer das
                     // nicht weiß, hält die Zahl für kaputt und die anderen
                     // gleich mit.
                     + (gesamt > 100
                        ? ` Mehr als der Umsatz dieses Zeitraums: Die `
                          + `Gutschriften beziehen sich auf Arbeit, die früher `
                          + `abgerechnet wurde.`
                        : "")}>
          {prozent(gesamt)} gesamt
        </span>
      )}
    </span>
  );
}


/**
 * Die Einordnung eines Kunden in Worten – für alle, die keine Umsatzzahlen
 * sehen dürfen.
 *
 * Bisher stand dort ein Strich. Damit fehlte den Leuten, die täglich mit den
 * Kunden sprechen, genau das, was über den Umgang entscheidet: Ist das ein
 * wichtiger Kunde? Hatte er oft Grund zur Beanstandung?
 *
 * Wer mit Zahlen arbeitet, liest "7,3 %" und weiß Bescheid. Wer nicht, liest
 * Rauschen. Deshalb Worte, drei Stufen, und zu jeder Stufe eine Handlung –
 * die steht im Kurzhinweis. Eine Einstufung allein wäre nur ein Urteil über
 * den Kunden; erst „vor Zusagen Rücksprache halten" macht daraus etwas,
 * womit jemand arbeiten kann.
 */
const EINORDNUNG_FARBEN = {
  blau: "border-blue-200 bg-blue-50 text-blue-800",
  gruen: "border-emerald-200 bg-emerald-50 text-emerald-800",
  gelb: "border-amber-200 bg-amber-50 text-amber-800",
  rot: "border-red-200 bg-red-50 text-red-800",
  grau: "border-slate-200 bg-slate-50 text-slate-500",
};

export function EinordnungBadge({ stufe, testId }) {
  if (!stufe?.text) return <span className="text-slate-300">—</span>;
  return (
    <span data-testid={testId} title={stufe.hinweis}
          className={`inline-flex items-center rounded-full border px-2 py-0.5
                      text-[11px] font-medium whitespace-nowrap
                      ${EINORDNUNG_FARBEN[stufe.farbe] || EINORDNUNG_FARBEN.grau}`}>
      {stufe.text}
    </span>
  );
}

/**
 * Dieselbe Einordnung ausführlich – für die Kundenakte.
 *
 * Dort ist Platz für den Handlungshinweis im Klartext. Im Vorbeigehen an
 * einer Tabellenzeile liest ihn niemand; wenn man die Akte öffnet, weil man
 * gleich mit dem Kunden spricht, dagegen schon.
 */
export function EinordnungKarte({ einordnung }) {
  if (!einordnung) return null;
  const teile = [einordnung.bedeutung, einordnung.zusammenarbeit].filter(Boolean);
  if (teile.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"
         data-testid="einordnung-karte">
      {teile.map((s, i) => (
        <div key={i} className={`rounded-md border p-3 ${
               EINORDNUNG_FARBEN[s.farbe] || EINORDNUNG_FARBEN.grau}`}>
          <div className="text-sm font-semibold">{s.text}</div>
          <div className="text-xs mt-1 leading-relaxed opacity-90">{s.hinweis}</div>
        </div>
      ))}
    </div>
  );
}
